import React, { PropTypes } from 'react';
import Helmet from 'react-helmet';
import B from 'bem-cn';

import Typing from '../src/components/typing';
import Slider from '../src/components/slider/slider';
import Benefits from '../src/components/benefits';
import Examples from '../src/components/examples';
import News from '../src/components/news';
import Technology from '../src/components/technology';
import OurStory from '../src/components/ourStory';
import Careers from '../src/components/careers';
import Header from '../src/components/header/header';

import { isScrolledIntoView } from '../src/utils/scroll';
import { pushEvent } from '../src/utils/gtagManager';
import getStaticContent from '../src/utils/getStaticContent';

import { Button } from 'react-toolbox/lib/button';
import ProgressBar from 'react-toolbox/lib/progress_bar';

import './index.scss';

const cl = B('MainPage');
const SQUARE_WIDTH = 40;
const SQUARE_HEIGHT = 40;
const SQUARE_SPACE = 10;
const MOBILE_SQUARE_WIDTH = 25;
const MOBILE_SQUARE_HEIGHT = 25;
const MOBILE_SQUARE_SPACE = 5;

const leftPlaceholders = [5, 5, 5, 5, 5];
const rightPlaceholders = [5, 5, 5, 5, 5];

const readerContentIdxList = require('../src/squares.json');

const defaultReaderSquare = {
  rowIdx: 0,
  itemIdx: 0
};

export default class MainPage extends React.Component {
  constructor(props) {
    super(props);

    this._refs = {};
    this.sectionScrollEvents = {};

    this.squareTypesList = {
      reader: [],
      content: []
    };

    this.state = {
      coloredContent: this.getReaderContentIdxList(
        defaultReaderSquare.rowIdx,
        defaultReaderSquare.itemIdx
      ),
      slider: {
        changeAttempts: 0,
        slideOnType: true,
        current: {
          ...defaultReaderSquare
        }
      },
      statusBar: {
        numChars: 0,
        showedChars: 0
      }
    };

    this.onCurrentSlideChange = this.onCurrentSlideChange.bind(this);
    this.onTypingRowChange = this.onTypingRowChange.bind(this);
    this.onReaderClick = this.onReaderClick.bind(this);

    this.metaContent = getStaticContent('meta', props.currentPageName);
    this.staticContent = getStaticContent('solution', props.currentPageName);
    this.benefitsContent = getStaticContent('benefits', props.currentPageName);

    this.setSliderRef = ref => {
      this._refSlider = ref;
    };
  }

  static propTypes = {
    displayType: PropTypes.string.isRequired,
    currentPageName: PropTypes.string.isRequired,
    showedOverlay: PropTypes.string.isRequired,
    setOverlay: PropTypes.func.isRequired
  };

  scrollHandler = () => {
    Object.keys(this._refs).forEach(key => {
      if (
        isScrolledIntoView(this._refs[key]) && !this.sectionScrollEvents[key]
      ) {
        this.sectionScrollEvents[key] = true; // trigger once per section
        pushEvent('section_viewport', `#${key}`);
      }
    });
  };

  componentDidMount() {
    window.addEventListener('scroll', this.scrollHandler);
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.scrollHandler);
  }

  getReaderContentIdxList(rowIdx, itemIdx) {
    return readerContentIdxList[rowIdx][itemIdx];
  }

  getContentRow(count, row, displayType) {
    let squareWidth = SQUARE_WIDTH;
    let squareHeight = SQUARE_HEIGHT;
    let squareSpace = SQUARE_SPACE;
    if (displayType === 'phone') {
      squareWidth = MOBILE_SQUARE_WIDTH;
      squareHeight = MOBILE_SQUARE_HEIGHT;
      squareSpace = MOBILE_SQUARE_SPACE;
    }
    const arr = [];
    const coloredItems = this.state.coloredContent.reduce((acc, item) => {
      if (item.rowIdx === row) {
        acc[item.itemIdx] = true;
      }

      return acc;
    }, {});

    for (let index = 0; index < count; index++) {
      const style = {
        right: (squareWidth + squareSpace) * index,
        top: (squareHeight + squareSpace) * row
      };
      if (displayType === 'phone') {
        style.top += squareHeight + squareSpace * 2 + 8;
      }
      if (displayType === 'medium') {
        style.top += squareHeight + squareSpace * 2 + 15;
      }
      const active = typeof coloredItems[index] !== 'undefined';

      arr.push(
        <Button
          className={cl('contentSquare')({ content: true, active })()}
          key={`${row}-${index}`}
          raised={active}
          flat={!active}
          style={style}
          disabled={!active}
          ripple={false}
        >
          <div
            className={cl('contentSquare__icon', {
              content: true,
              active,
              item: row * 5 + (index + 1)
            })}
          />
        </Button>
      );
    }

    return arr;
  }

  getReaderRow(count, row, displayType) {
    let squareWidth = SQUARE_WIDTH;
    let squareHeight = SQUARE_HEIGHT;
    let squareSpace = SQUARE_SPACE;
    if (displayType === 'phone') {
      squareWidth = MOBILE_SQUARE_WIDTH;
      squareHeight = MOBILE_SQUARE_HEIGHT;
      squareSpace = MOBILE_SQUARE_SPACE;
    }
    const arr = [];
    for (let index = 0; index < count; index++) {
      const style = {
        left: (squareWidth + squareSpace) * index,
        top: (squareHeight + squareSpace) * row
      };
      const active =
        this.state.slider.current.rowIdx === row &&
        this.state.slider.current.itemIdx === index;

      if (displayType) {
        style.left = 'auto';
        style.top = 0;
        if (active) {
          style.right = (squareWidth + squareSpace) * 2;
        } else {
          if (this.state.slider.current.rowIdx === row) {
            if (Math.abs(index - this.state.slider.current.itemIdx) > 2) {
              continue;
            }
            if (index < this.state.slider.current.itemIdx) {
              style.right =
                (squareWidth + squareSpace) *
                (2 + this.state.slider.current.itemIdx - index);
            }
            if (index > this.state.slider.current.itemIdx) {
              style.right =
                (squareWidth + squareSpace) *
                (2 - (index - this.state.slider.current.itemIdx));
            }
          }
          if (this.state.slider.current.rowIdx < row) {
            if (this.state.slider.current.rowIdx === row - 1) {
              const position = 5 - this.state.slider.current.itemIdx + index;
              if (position > 2) {
                continue;
              }
              style.right = (squareWidth + squareSpace) * (2 - position);
            } else {
              if (this.state.slider.current.rowIdx === 0 && row === 4) {
                const position = 5 - index + this.state.slider.current.itemIdx;
                if (position > 2) {
                  continue;
                }
                style.right = (squareWidth + squareSpace) * (position + 2);
              } else {
                continue;
              }
            }
          }
          if (this.state.slider.current.rowIdx > row) {
            if (this.state.slider.current.rowIdx === row + 1) {
              const position = 5 - index + this.state.slider.current.itemIdx;
              if (position > 2) {
                continue;
              }
              style.right = (squareWidth + squareSpace) * (position + 2);
            } else {
              if (this.state.slider.current.rowIdx === 4 && row === 0) {
                const position = 5 - this.state.slider.current.itemIdx + index;
                if (position > 2) {
                  continue;
                }
                style.right = (squareWidth + squareSpace) * (2 - position);
              } else {
                continue;
              }
            }
          }
        }
      }

      const onClickHandler = ['phone', 'medium'].indexOf(displayType) !== -1
        ? null
        : e => {
          this.onReaderClick(e, row, index);
        };

      arr.push(
        <Button
          className={cl('contentSquare')({ active, reader: true })()}
          key={`${row}-${index}`}
          raised={active}
          flat={!active}
          style={style}
          ripple={['phone', 'medium'].indexOf(displayType) === -1}
          onClick={onClickHandler}
        >
          <div
            className={cl('contentSquare__icon', {
              reader: true,
              active,
              item: row * 5 + (index + 1)
            })}
          />
        </Button>
      );
    }

    return arr;
  }

  onCurrentSlideChange(e, rowIdx, itemIdx, direction) {
    // Check event was automatic (true) or manual (false)
    const slideOnType = e && e.type === 'typing_change_synthetic_event'
      ? true
      : false;

    if (slideOnType) {
      pushEvent('slider_change', 'typewriter');
    } else {
      pushEvent(
        'slider_click',
        direction === 'prev' ? 'button_previous' : 'button_next'
      );
    }

    this.setState({
      coloredContent: this.getReaderContentIdxList(rowIdx, itemIdx),
      slider: {
        changeAttempts: 0,
        slideOnType,
        current: {
          rowIdx,
          itemIdx
        }
      }
    });
  }

  onTypingRowChange() {
    const { slideOnType, changeAttempts } = this.state.slider;
    if (slideOnType || changeAttempts === 3) {
      this._refSlider.slideNext({
        type: 'typing_change_synthetic_event'
      });
    } else {
      if (!slideOnType) {
        this.setState(state => ({
          ...state,
          slider: {
            ...state.slider,
            changeAttempts: state.slider.changeAttempts + 1
          }
        }));
      }
    }
  }

  onReaderClick(e, row, item) {
    const obj = { rowIdx: row, itemIdx: item };
    this._refSlider.setActiveSlide(obj);
    this.setState({
      coloredContent: this.getReaderContentIdxList(row, item),
      slider: {
        changeAttempts: 0,
        slideOnType: false,
        current: { ...obj }
      }
    });
  }

  onTypingStartNewRow = text => {
    this.setState({
      statusBar: {
        numChars: text.length,
        showedChars: 0
      }
    });
  };

  onTypingShowNewChar = () => {
    this.setState(state => ({
      statusBar: {
        ...state.statusBar,
        showedChars: state.statusBar.showedChars + 1
      }
    }));
  };

  getHelmet() {
    return (
      <Helmet>
        <title>{this.metaContent.title}</title>
        <link rel="canonical" href={this.metaContent.canonical} />
        <meta name="robots" content="index, follow" />
      </Helmet>
    );
  }

  render() {
    const { statusBar } = this.state;
    const {
      displayType,
      showedOverlay,
      setOverlay,
      currentPageName
    } = this.props;

    return (
      <div>
        <Header
          setOverlay={setOverlay}
          displayType={displayType}
          showedOverlay={showedOverlay}
          currentPageName={currentPageName}
        />
        <div className={cl}>
          {this.getHelmet()}

          <div
            id="solution"
            className={cl('container', { solutions: true }).mix(
              'container-fluid'
            )}
            ref={ref => this._refs.solution = ref}
          >
            <div className={cl('content')}>
              <div className="hidden-xs hidden-sm">
                {rightPlaceholders.map((count, row) =>
                  this.getReaderRow(count, row)
                )}
                {leftPlaceholders.map((count, row) =>
                  this.getContentRow(count, row)
                )}
              </div>

              <div className="hidden-xs hidden-sm">
                <div className={cl('arrowHint', { readers: true })}>
                  {this.staticContent.leftHint}
                </div>
                <div className={cl('arrowHint', { content: true })}>
                  {this.staticContent.rightHint}
                </div>
              </div>

              <div className={cl('mainSquare')}>
                <div className={cl('mainDescription')}>
                  <div className={cl('mainDescriptionTitle')}>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: this.staticContent.title
                      }}
                    />
                    <div
                      dangerouslySetInnerHTML={{
                        __html: this.staticContent.subtitle
                      }}
                    />
                  </div>
                  <div className={cl('typewriter').mix('hidden-xs')}>
                    <Typing
                      text={this.benefitsContent.content}
                      onRowChange={this.onTypingRowChange}
                      onStartNewRow={this.onTypingStartNewRow}
                      onShowNewChar={this.onTypingShowNewChar}
                    />
                  </div>
                </div>

                <div className={cl('slider-container')}>
                  <div>
                    <div
                      className={cl('arrowHint', { website: true }).mix(
                        'hidden-md hidden-lg'
                      )}
                    >
                      Your Website
                    </div>
                    <Slider
                      ref={this.setSliderRef}
                      init={defaultReaderSquare}
                      items={rightPlaceholders}
                      onChange={this.onCurrentSlideChange}
                      readerContentIdxList={readerContentIdxList}
                      currentPageName={currentPageName}
                    />
                  </div>
                  <div
                    className={cl('slider-container')('readers-contents').mix(
                      'hidden-md hidden-lg'
                    )}
                  >
                    <div className={cl('arrowHint', { readers: true })}>
                      {this.staticContent.leftHint}
                    </div>
                    <div className={cl('arrowHint', { content: true })}>
                      {this.staticContent.rightHint}
                    </div>
                    {rightPlaceholders.map((count, row) =>
                      this.getReaderRow(count, row, displayType)
                    )}
                    <div
                      className={cl('slider-container')('readers-contents')(
                        'status-bar'
                      )}
                    >
                      <ProgressBar
                        className={cl('slider-container')('readers-contents')(
                          'status-bar'
                        )('progress')()}
                        mode="determinate"
                        value={Math.ceil(
                          100 / statusBar.numChars * statusBar.showedChars
                        )}
                      />
                    </div>
                    {leftPlaceholders.map((count, row) =>
                      this.getContentRow(count, row, displayType)
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            ref={ref => this._refs.benefits = ref}
            className={cl('container').mix([
              'container-fluid',
              cl('benefits-container')
            ])}
          >
            <Benefits currentPageName={currentPageName} />
          </div>
          <Examples
            passRef={ref => this._refs.examples = ref}
            currentPageName={currentPageName}
          />
          <OurStory
            passRef={ref => this._refs.ourstory = ref}
            currentPageName={currentPageName}
          />
          <Technology
            passRef={ref => this._refs.technology = ref}
            currentPageName={currentPageName}
          />
          <News
            passRef={ref => this._refs.news = ref}
            currentPageName={currentPageName}
          />
          <Careers
            passRef={ref => this._refs.careers = ref}
            currentPageName={currentPageName}
          />

        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./_mainPage.jsx